/**
 * @author Vibhor <vibhor.sharma@314ecorp.com>
 * @description Claim Filing Store
 */
import _ from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

const secondaryFilingInitialState: ClaimFiling.ISecondaryFilingValues = {
	payerInfo: null,
	subscriberInfo: null,
	coverageInfo: null,
};

const primaryFilingInitialState: ClaimFiling.IPrimaryFilingValues = {
	selectedEntities: {},
	institutionalFormData: null,
	professionalFormData: null,
	invalidHcpcsCodes: [],
};

const initialState: ClaimFiling.IValues = {
	...secondaryFilingInitialState,
	...primaryFilingInitialState,
};

const useClaimFilingStore = create<ClaimFiling.IStore>()(
	persist(
		devtools(
			(set) => ({
				...initialState,
				actions: {
					// Secondary filing actions
					setPayerInfo: (payerInfo) => set(() => ({ payerInfo })),
					setSubscriberInfo: (subscriberInfo) => set(() => ({ subscriberInfo })),
					setCoverageInfo: (coverageInfo) => set(() => ({ coverageInfo })),

					// Primary filing actions
					setSelectedEntities: (selectedEntities) => set(() => ({ selectedEntities })),
					setSelectedPatient: (patient) =>
						set((state) => ({
							selectedEntities: {
								...state.selectedEntities,
								patient: patient || undefined,
								// reset dependent fields when patient changes
								account: undefined,
								insurance: undefined,
								billingProvider: undefined,
							},
						})),
					setSelectedAccount: (account) =>
						set((state) => ({
							selectedEntities: {
								...state.selectedEntities,
								account: account || undefined,
							},
						})),
					setSelectedInsurance: (insurance) =>
						set((state) => ({
							selectedEntities: {
								...state.selectedEntities,
								insurance: insurance || undefined,
							},
						})),
					setSelectedBillingProvider: (provider) =>
						set((state) => ({
							selectedEntities: {
								...state.selectedEntities,
								billingProvider: provider || undefined,
							},
						})),
					setInstitutionalFormData: (formData) => set(() => ({ institutionalFormData: formData })),
					setProfessionalFormData: (formData) => set(() => ({ professionalFormData: formData })),
					setInvalidHcpcsCodes: (codes) => set(() => ({ invalidHcpcsCodes: codes })),
					resetPrimaryFilingState: () =>
						set(() => ({
							selectedEntities: {},
							institutionalFormData: null,
							professionalFormData: null,
							invalidHcpcsCodes: [],
						})),

					// reset all state
					resetState: () => set(() => initialState),
				},
			}),
			{ name: 'Claim Filing Store' },
		),
		{
			name: 'claim-filing-store',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				// secondary filing
				payerInfo: state.payerInfo,
				subscriberInfo: state.subscriberInfo,
				coverageInfo: state.coverageInfo,
				// primary filing
				selectedEntities: state.selectedEntities,
				institutionalFormData: state.institutionalFormData,
				professionalFormData: state.professionalFormData,
				invalidHcpcsCodes: state.invalidHcpcsCodes,
			}),
		},
	),
);

// Export hooks
const useClaimFilingStoreValues = (): ClaimFiling.IValues => useClaimFilingStore(useShallow((state) => _.omit(state, ['actions'])));
const useClaimFilingStoreActions = (): ClaimFiling.IActions => useClaimFilingStore(useShallow((state) => state.actions));

export { useClaimFilingStoreValues, useClaimFilingStoreActions };
export default useClaimFilingStore;
